import { useCallback, useEffect, useMemo, useState } from 'react';
import useData from '@/common/data';
import { getSessionStorage, setSessionStorage } from '@/common/sessionStorages';
import { useMutation, useQuery } from 'react-query';
import { requestNextApi } from '@/utils/next-api';
import { useBreakPointDown } from '@/hooks/breakPoint';
import { getPasscodeSupportedPlatforms } from '@/modules/auth/utils/passcode-authenticator';

type THasPasscodeData = {
  hasPasscode: boolean;
};

type TUseHasPasscodeParam = {
  onChecked?: (__param: THasPasscodeData) => void;
};

export const useAddPasscodeMutation = () => {
  return useMutation<unknown, unknown, string>(async (newPasscode) => {
    return requestNextApi('auth/passcode/add', {
      method: 'POST',
      data: { newPasscode },
    });
  });
};

export const useRemovePasscodeMutation = () => {
  return useMutation<unknown, unknown, string>(async (currentPasscode) => {
    return requestNextApi('auth/passcode/remove', {
      method: 'DELETE',
      data: { currentPasscode },
    });
  });
};

export const useVerifyPasscodeMutation = () => {
  return useMutation<unknown, unknown, string>(async (currentPasscode) => {
    return requestNextApi('auth/passcode/verify', {
      method: 'POST',
      data: { currentPasscode },
    });
  });
};

//----------------------------------------------------------------------------
//----------------------------------------------------------------------------

export const useIsPasscodeSupported = () => {
  const supportedPlatforms = getPasscodeSupportedPlatforms();
  const isMobileOrTable = useBreakPointDown('lg');
  return supportedPlatforms[isMobileOrTable ? 'mobileAndTablet' : 'desktop'];
};

export const useHasPasscode = ({ onChecked }: TUseHasPasscodeParam = {}) => {
  const isPasscodeSupported = useIsPasscodeSupported();

  const result = useQuery<THasPasscodeData>(
    ['___', 'passcode'],
    async () => requestNextApi('auth/passcode'),
    {
      enabled: isPasscodeSupported,
      cacheTime: Infinity,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      notifyOnChangeProps: ['isFetching', 'data'],
    }
  );

  return useMemo(() => {
    const { isFetching, refetch } = result;
    const data = result?.data ?? ({} as THasPasscodeData);
    if (!isFetching) onChecked?.({ ...data });
    return { ...data, refetch, isPasscodeSupported, isLoading: isFetching };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPasscodeSupported, result]);
};

export const useIsNeedVerifyPasscodeState = (): [
  boolean,
  (__v: boolean) => void,
] => {
  const { data: isNeedVerifyPasscode, setData: setIsNeedVerifyPasscode } =
    useData('isNeedVerifyPasscode', { initialData: false });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return [isNeedVerifyPasscode, useCallback(setIsNeedVerifyPasscode, [])];
};

export const useIsNeedVerifyPasscodeListener = (
  shouldPopupOnInitialLoad: boolean = true
) => {
  const supportedPlatforms = getPasscodeSupportedPlatforms();
  const isPasscodeNeedHighSecurity: boolean = supportedPlatforms.desktop;
  const [isNeedVerifyPasscode, setIsNeedVerifyPasscode] =
    useIsNeedVerifyPasscodeState();
  const [blurTime, setBlurTime] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { hasPasscode } = useHasPasscode({
    onChecked: ({ hasPasscode }) => {
      if (isLoading) {
        setIsLoading(false);
        // Handles cases where the useIsNeedVerifyPasscodeListener hook is invoked within deeper component layers,
        // preventing authentication from triggering on the initial load and avoiding re-renders.
        if (shouldPopupOnInitialLoad) handleOnLoad(hasPasscode);
      }
    },
  });

  function handleOnLoad(hasPasscode: boolean) {
    if (!isPasscodeNeedHighSecurity && getSessionStorage('PV1OM')) return;
    setIsNeedVerifyPasscode(hasPasscode);
  }

  function handleVerify() {
    setIsNeedVerifyPasscode(false);

    if (!isPasscodeNeedHighSecurity) {
      setSessionStorage('PV1OM', true);
    }
  }

  useEffect(() => {
    const handleBlur = () => {
      setBlurTime(Date.now());
    };

    const handleFocus = () => {
      const timeDiffSeconds = Math.ceil((Date.now() - blurTime) / 1000);
      const intervalSeconds: number =
        Number(process.env.NEXT_PUBLIC_PASSCODE_TIMEOUT_SEC) || 0;
      if (timeDiffSeconds > intervalSeconds) {
        setIsNeedVerifyPasscode(hasPasscode);
      }
    };

    const handleVisibility = () => {
      const isVisible: boolean = document.visibilityState === 'visible';
      if (isVisible) {
        handleFocus();
      } else {
        handleBlur();
      }
    };

    window.addEventListener('visibilitychange', handleVisibility, false);
    window.addEventListener('focus', handleFocus, false);
    window.addEventListener('blur', handleBlur, false);
    return () => {
      window.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleFocus);
    };
  }, [blurTime, hasPasscode, setIsNeedVerifyPasscode]);

  return {
    isNeedVerifyPasscode,
    handleVerify,
    isLoading,
    supportedPlatforms,
    isPasscodeNeedHighSecurity,
    setIsNeedVerifyPasscode,
  };
};
